import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { atom, useAtom, useSetAtom } from 'jotai';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'next-i18next';

import { useWorkersService } from '@restworld/data-services';
import { useAuth } from '@restworld/feature-authentication';
import { Button, showToast, Modal, ModalContent } from '@restworld/ui-ds-base';
import { QUERY_KEYS } from '@restworld/utils-common';

import { referencesRequestSuccessMessageVisibleAtom } from './references-request-message-modal';
import {
  selectedReferencesDataAtom,
  referencesSendLinkRequestModalVisibleAtom,
} from './references-send-link-request-modal';
import { referencesModalClassName } from './references-consent-modal';
import { isReferencesContentGeneralFlowAtom } from './profile-details-section';
import { ReferenceRequestCard } from '../legacy';

export const referencesReqSelectorModalVisibleAtom = atom<boolean>(false);

type ListWorkerPastExperiencesQueryParams = {
  limit: number;
  offset: number;
};
const useListWorkerPastExperiencesQuery = (params?: ListWorkerPastExperiencesQueryParams) => {
  const { user } = useAuth();
  const workerService = useWorkersService();

  const query = useQuery({
    queryKey: [QUERY_KEYS.WORKERS.FETCH_WORKER_PAST_EXPERIENCES],
    queryFn: async () => {
      return (await workerService.listWorkerPastExperiences({ ...params, workerId: user?.id })).data.data;
    },
    refetchIntervalInBackground: true,
  });

  return query;
};

// The backend answers 409 with this code when every past-experience id we sent has been deleted.
// A CV re-parse replaces a worker's whole past_experiences collection, so the list this modal was
// built from can go stale seconds after it was fetched.
const STALE_PAST_EXPERIENCE_IDS = 'stale_past_experience_ids';

export const useListWorkerPastExperiencesMutation = () => {
  const workerService = useWorkersService();

  return useMutation({
    mutationFn: async (pastExperiences: { id: string; collect_references: boolean }[]) => {
      return (await workerService.updateWorkerPastExperience({ past_experiences: pastExperiences })).data;
    },
    // Until now this mutation had no error handler at all: the modal only closes on success, so a
    // failure left the worker looking at a button that did nothing, and they clicked it again (22
    // times in 50 seconds, in one production case). Reloading is the whole fix for the stale case,
    // because every id the modal is holding is dead and a refetch is the only way forward.
    //
    // The id lives at `error.data.code`, not `error.code`: the axios interceptor reshapes errors to
    // `{ code: <http status>, message, data: <response body> }`, so `error.code` here is 409.
    onError: (error: { data?: { code?: string } }) => {
      if (error?.data?.code === STALE_PAST_EXPERIENCE_IDS) window.location.reload();
    },
  });
};

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IReferenceRequestSelectorModalProps {}
const ReferenceRequestSelectorModal: React.FunctionComponent<IReferenceRequestSelectorModalProps> = (props) => {
  const [isVisible, setIsVisible] = useAtom(referencesReqSelectorModalVisibleAtom);
  const setReferencesSendLinkRequestModalVisible = useSetAtom(referencesSendLinkRequestModalVisibleAtom);
  const setReferencesRequestSuccessMessageVisible = useSetAtom(referencesRequestSuccessMessageVisibleAtom);
  const setReferencesReqSelectorModalVisible = useSetAtom(referencesReqSelectorModalVisibleAtom);
  const setSelectedReferencesData = useSetAtom(selectedReferencesDataAtom);
  const [isReferencesContentGeneralFlow, setIsReferencesContentGeneralFlow] = useAtom(
    isReferencesContentGeneralFlowAtom
  );
  const { data, isLoading, refetch } = useListWorkerPastExperiencesQuery({ limit: 20, offset: 0 });

  const queryClient = useQueryClient();

  const methods = useForm<{
    selectedReferenceIds: string[];
  }>({
    defaultValues: {
      selectedReferenceIds: [],
    },
  });
  const overallReferenceRequestIds = React.useMemo(() => data?.map((experience) => experience.id) || [], [data]);
  const defaultSelectedReferenceIds = React.useMemo(() => {
    if (!data) return [];
    return data
      .filter((v, i) => (isReferencesContentGeneralFlow ? i !== 0 && !v.currently_working : v.collect_references))
      .map((experience) => experience.id);
  }, [data, isReferencesContentGeneralFlow]);

  React.useEffect(() => {
    refetch();
  }, [refetch]);

  React.useEffect(() => {
    if (isVisible && overallReferenceRequestIds?.length > 0) {
      methods.reset({
        selectedReferenceIds: defaultSelectedReferenceIds,
      });
    }
  }, [defaultSelectedReferenceIds, isVisible, methods, overallReferenceRequestIds]);

  const {
    mutate: createReferenceRequests,
    isPending,
    isSuccess: isWorkerPastUpdateSuccess,
    reset: resetWorkerPastUpdate,
  } = useListWorkerPastExperiencesMutation();

  const handleShareLink = React.useCallback(
    (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, { experienceId, venueName }) => {
      e.preventDefault();
      setSelectedReferencesData({ experienceId, venueName });
      setIsVisible(false);
      setReferencesSendLinkRequestModalVisible(true);
    },
    [setIsVisible, setReferencesSendLinkRequestModalVisible, setSelectedReferencesData]
  );

  const onStateReset = React.useCallback(() => {
    setIsVisible(false);
    setSelectedReferencesData({ experienceId: '', venueName: '' });
    methods.reset({ selectedReferenceIds: [] });
  }, [methods, setIsVisible, setSelectedReferencesData]);

  const handleSubmit = methods.handleSubmit(({ selectedReferenceIds }) => {
    const payload = overallReferenceRequestIds?.map((id) => ({
      id: id,
      collect_references: selectedReferenceIds.includes(id),
    }));
    createReferenceRequests(payload);
  });

  const { t } = useTranslation('worker-references-page');

  const onWorkerPastUpdateSuccess = React.useCallback(() => {
    setReferencesReqSelectorModalVisible(false);
    queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.WORKERS.FETCH_WORKER_PAST_EXPERIENCES] });
    if (isReferencesContentGeneralFlow) {
      setReferencesRequestSuccessMessageVisible(true);
    } else {
      showToast(t('references_updated_successfully'), 'success', {
        autoClose: 3000,
        position: 'top-right',
      });
    }
    resetWorkerPastUpdate();
    setIsReferencesContentGeneralFlow(false);
  }, [
    isReferencesContentGeneralFlow,
    queryClient,
    resetWorkerPastUpdate,
    setReferencesReqSelectorModalVisible,
    setReferencesRequestSuccessMessageVisible,
    setIsReferencesContentGeneralFlow,
    t,
  ]);

  React.useEffect(() => {
    if (isWorkerPastUpdateSuccess) onWorkerPastUpdateSuccess();
  }, [isWorkerPastUpdateSuccess, onWorkerPastUpdateSuccess]);

  return (
    <Modal open={isVisible} onOpenChange={(v) => !v && onStateReset()}>
      <ModalContent
        noPadding
        className={referencesModalClassName}
        title={
          <h5 className="px-4 lg:px-[40px] self-stretch text-center text-neutral-700 text-lg font-semibold">
            {t('references_request_list.title')}
          </h5>
        }
        footer={
          <div className="px-4 lg:px-[40px] w-full border-t border-neutral-40 flex flex-col justify-start gap-4 py-4">
            <Button
              fullwidth
              variant="primary"
              onClick={handleSubmit}
              disabled={isLoading || isPending || !data || data?.length === 0}
            >
              {t('references_request_list.footer.button.primary')}
            </Button>
          </div>
        }
      >
        <form action="#!" method="post" className="px-4 lg:px-[40px] w-full flex flex-col justify-start gap-3 py-9">
          <Controller
            control={methods.control}
            name="selectedReferenceIds"
            render={({ field }) => {
              return (
                <>
                  {data?.map((experience, index) => (
                    <ReferenceRequestCard
                      key={experience.id}
                      role={experience.occupation?.label_it?.label}
                      venueName={experience.restaurant_name}
                      fromDate={experience.from_date}
                      toDate={experience.to_date}
                      {...{
                        ...field,
                        onChange: (checked: boolean) => {
                          const currentValue = field.value || [];
                          if (checked) {
                            field.onChange([...currentValue, experience.id]);
                          } else {
                            field.onChange(currentValue.filter((id: string) => id !== experience.id));
                          }
                        },
                        checked: field.value?.includes(experience.id) || false,
                      }}
                      handleShareLink={(e) =>
                        handleShareLink(e, {
                          experienceId: experience.id,
                          venueName: experience.restaurant_name,
                        })
                      }
                      disableShareLink={
                        !field.value?.includes(experience.id) || !experience.collect_references || false
                      }
                      isShareable={!isReferencesContentGeneralFlow ? field.value?.includes(experience.id) : false}
                      showTooltip={!experience.collect_references}
                    />
                  ))}
                </>
              );
            }}
          />
        </form>
      </ModalContent>
    </Modal>
  );
};

export default ReferenceRequestSelectorModal;
